'use client';

import { IconCheckCircle, IconHappy, IconHappyDarker, IconSad, IconSadDarker } from '@mv/icons/web';
import { Pill, VisuallyHidden } from '@mv/web-ui';
import { useState } from 'react';
import { useI18n } from '@/modules/i18n/useI18n';
import { useDataTags } from '@/modules/tracking/analytics/useDataTags';
import RichText from '@/payload/components/RichText/RichText';
import styles from './Feedback.module.scss';

type FeedbackProps = {
	dataTag?: {
		label?: string;
		values?: Record<string, unknown>;
	};
};
function Feedback({ dataTag }: FeedbackProps) {
	const { blockRef, labelRef } = useDataTags('feedback', null, dataTag?.values);
	const { terms } = useI18n();
	const [status, setStatus] = useState<'success' | null>(null);
	const [selectedChoice, setSelectedChoice] = useState<'yes' | 'no' | null>(null);

	const onChangeChoice = (choice: 'yes' | 'no') => {
		if (selectedChoice === choice) return;
		setSelectedChoice(choice);
		setStatus('success');
	};

	return (
		<div className={`${styles.feedbackContainer} ${styles.inline}`} data-component="Feedback" ref={blockRef}>
			<VisuallyHidden>
				<span ref={labelRef} aria-hidden>
					{dataTag?.label}
				</span>
			</VisuallyHidden>
			<div className={styles.buttons}>
				<Pill
					shadow
					active={selectedChoice === 'yes'}
					disabled={selectedChoice === 'no'}
					onClick={() => onChangeChoice('yes')}
					data-tag-value="yes"
				>
					{selectedChoice === 'yes' ? <IconHappyDarker /> : <IconHappy />} {terms.feedback.understood}
				</Pill>

				<Pill
					shadow
					active={selectedChoice === 'no'}
					disabled={selectedChoice === 'yes'}
					onClick={() => onChangeChoice('no')}
					data-tag-value="no"
				>
					{selectedChoice === 'no' ? <IconSadDarker /> : <IconSad />} {terms.feedback.notUseful}
				</Pill>
			</div>

			{status && (
				<div className={styles.message}>
					{status === 'success' && (
						<>
							<IconCheckCircle />
							<RichText options={{ size: 'small' }} content={terms.feedback.success ?? null} />
						</>
					)}
				</div>
			)}
		</div>
	);
}

export default Feedback;
